import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from 'src/app/shared/page-layout/page-detail-hero.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PosmDeliverySchedule, PosmDeliveryScheduleAllocation, PosmProjectStore } from '../../models/posm-execution.models';
import { PosmProjectItem, PosmProjectItemSource, PosmProjectItemSourceReservation } from '../../models/posm-project-item.model';
import { PosmProject } from '../../models/posm-project.model';
import { PosmService } from '../../posm.service';
import { PosmScheduleCompletionReportComponent } from '../../posm-delivery-schedules/posm-schedule-completion-report.component';
import { WarehousingMasterData } from 'src/app/warehousing/warehousing.model';
import { WarehousingService } from 'src/app/warehousing/warehousing.service';
import { FieldResourcesPanelComponent } from 'src/app/field-work/field-resources-panel.component';

@Component({
    selector: 'app-posm-project-delivery-schedule-detail',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputTextModule,
        SelectModule,
        TagModule,
        TextareaModule,
        ToastModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageDataPanelComponent,
        PosmScheduleCompletionReportComponent,
        FieldResourcesPanelComponent
    ],
    providers: [MessageService],
    templateUrl: './posm-delivery-schedule-detail.component.html',
    styleUrls: ['./posm-delivery-schedule-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmDeliveryScheduleDetailComponent implements OnInit {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private sanitizer = inject(DomSanitizer);
    private posmService = inject(PosmService);
    private warehousingService = inject(WarehousingService);
    private sessionStorage = inject(SessionStorageService);
    private messageService = inject(MessageService);
    private permissionService = inject(PermissionService);

    public project = signal<PosmProject | null>(null);
    public schedule = signal<PosmDeliverySchedule>({});
    public stores = signal<PosmProjectStore[]>([]);
    public items = signal<PosmProjectItem[]>([]);
    public zones = signal<WarehousingMasterData[]>([]);
    public conditions = signal<WarehousingMasterData[]>([]);
    public allocations = signal<PosmDeliveryScheduleAllocation[]>([]);
    public allocationCandidates = signal<PosmDeliveryScheduleAllocation[]>([]);
    public allocationSources = signal<Record<number, PosmProjectItemSource>>({});
    public allocationQuantities = signal<Record<number, number>>({});
    public returnOutcomeZoneId = signal<number | null>(null);
    public returnOutcomeConditionId = signal<number | null>(null);
    public returnOutcomeNotes = signal('');
    public loadingAllocations = signal(false);
    public savingAllocationId = signal<number | null>(null);
    public loading = signal(false);
    public completingReturnOutcome = signal(false);
    public showViewerDialog = signal(false);
    public viewerDialogHeader = signal('Open Record');
    public viewerDialogUrl = signal<SafeResourceUrl | null>(null);

    public projectId?: number;
    public lineId?: number;
    public companyId?: number;

    public returnConditionOptions = computed(() => this.conditions().map(condition => ({
        label: [condition.name, condition.code].filter(Boolean).join(' | '),
        value: condition.id
    })));
    public returnZoneOptions = computed(() => {
        const warehouseId = this.effectiveReturnWarehouseId();
        return this.zones()
            .filter(zone => !warehouseId || zone.parentId === warehouseId)
            .map(zone => ({
                label: [zone.name, zone.code].filter(Boolean).join(' | '),
                value: zone.id
            }));
    });
    public selectedStore = computed(() => this.stores().find(store => store.id === this.schedule().storeId));
    public selectedItem = computed(() => this.items().find(item => item.id === this.schedule().projectItemId));
    public normalizedJobTypes = computed(() => {
        const project = this.project();
        const values = [
            ...(project?.jobTypes || []),
            ...(project?.jobTypeCodes || []),
            project?.jobType
        ].filter((value): value is string => !!value);
        const normalized = values
            .map(value => `${value}`.trim().toLowerCase())
            .filter(Boolean);
        return [...new Set(normalized)];
    });
    public supportsRemovalFlow = computed(() => {
        return this.normalizedJobTypes().some(type => type === 'removal' || type === 'collection');
    });

    ngOnInit(): void {
        this.projectId = Number(this.route.snapshot.paramMap.get('projectId'));
        this.lineId = Number(this.route.snapshot.paramMap.get('lineId'));
        this.companyId = this.sessionStorage.getCompany()?.id;
        if (!this.projectId || !this.companyId || !this.lineId) {
            this.backToSchedule();
            return;
        }
        this.load();
    }

    load(): void {
        if (!this.projectId || !this.companyId) return;
        this.loading.set(true);
        this.posmService.getProject(this.projectId, this.companyId).subscribe({
            next: project => {
                this.project.set(project);
            },
            error: () => this.messageService.add({ severity: 'warn', summary: 'Project', detail: 'Unable to load POSM project.' })
        });
        this.posmService.getProjectStores(this.projectId, this.companyId).subscribe({
            next: stores => this.stores.set(stores || []),
            error: () => this.stores.set([])
        });
        this.posmService.getProjectItems(this.projectId, this.companyId).subscribe({
            next: items => this.items.set(items || []),
            error: () => this.items.set([])
        });
        this.warehousingService.getMasterData('ZONE', { activeStatus: 'ACTIVE' }).subscribe({
            next: response => this.zones.set(response?.data || []),
            error: () => this.zones.set([])
        });
        this.warehousingService.getMasterData('ITEM_CONDITION', { activeStatus: 'ACTIVE' }).subscribe({
            next: response => this.conditions.set(response?.data || []),
            error: () => this.conditions.set([])
        });
        this.posmService.getProjectDeliverySchedule(this.projectId, this.lineId!, this.companyId)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: row => {
                    this.schedule.set(this.normalizeRow(row));
                    this.returnOutcomeConditionId.set(row.returnConditionId || null);
                    this.returnOutcomeZoneId.set(null);
                    this.returnOutcomeNotes.set('');
                    this.loadAllocations();
                    this.loadAllocationSources();
                },
                error: () => {
                    this.messageService.add({ severity: 'error', summary: 'Delivery Schedule', detail: 'Unable to load delivery schedule row.' });
                    this.backToSchedule();
                }
            });
    }

    loadAllocations(): void {
        if (!this.projectId || !this.companyId || !this.lineId) return;
        this.loadingAllocations.set(true);
        this.posmService.getDeliveryScheduleAllocations(this.projectId, this.lineId, this.companyId).subscribe({
            next: allocations => this.allocations.set(allocations || []),
            error: () => {
                this.allocations.set([]);
                this.messageService.add({ severity: 'warn', summary: 'Allocations', detail: 'Unable to load material allocations.' });
            }
        });
        this.posmService.getDeliveryScheduleAllocationCandidates(this.projectId, this.lineId, this.companyId)
            .pipe(finalize(() => this.loadingAllocations.set(false)))
            .subscribe({
                next: candidates => {
                    this.allocationCandidates.set(candidates || []);
                    const defaults: Record<number, number> = {};
                    (candidates || []).forEach(candidate => {
                        const key = this.allocationRequestKey(candidate);
                        if (key) {
                            defaults[key] = Math.min(
                                this.remainingAllocationQuantity(),
                                Number(candidate.availableToAllocate || 0)
                            ) || Number(candidate.availableToAllocate || 0);
                        }
                    });
                    this.allocationQuantities.set(defaults);
                },
                error: () => {
                    this.allocationCandidates.set([]);
                    this.messageService.add({ severity: 'warn', summary: 'Allocations', detail: 'Unable to load reserved stock unit candidates.' });
                }
            });
    }

    loadAllocationSources(): void {
        if (!this.projectId || !this.companyId || !this.schedule().projectItemId) {
            this.allocationSources.set({});
            return;
        }
        this.posmService.getProjectItemSources(this.projectId, this.schedule().projectItemId!, this.companyId).subscribe({
            next: sources => {
                const mapped = Object.fromEntries((sources || [])
                    .filter(source => !!source.id)
                    .map(source => [source.id!, source]));
                this.allocationSources.set(mapped);
            },
            error: () => this.allocationSources.set({})
        });
    }

    allocateCandidate(candidate: PosmDeliveryScheduleAllocation): void {
        if (!this.projectId || !this.companyId || !this.lineId || !candidate.projectItemSourceId) return;
        const requestKey = this.allocationRequestKey(candidate);
        const quantity = Number(this.allocationQuantities()[requestKey || 0] || 0);
        const maxAllowed = this.maxAllocationForCandidate(candidate);
        if (quantity <= 0 || quantity > maxAllowed) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Allocation',
                detail: 'Enter an allocation quantity within the available reserved quantity and remaining schedule requirement.'
            });
            return;
        }
        this.savingAllocationId.set(requestKey);
        this.posmService.createDeliveryScheduleAllocation(this.projectId, this.lineId, this.companyId, {
            projectItemSourceId: candidate.projectItemSourceId,
            reservationAllocationId: candidate.reservationAllocationId,
            stockUnitId: candidate.stockUnitId,
            allocatedQuantity: quantity,
            notes: `Allocated to ${this.schedule().scheduleNumber || 'delivery schedule'}`
        }).pipe(finalize(() => this.savingAllocationId.set(null))).subscribe({
            next: () => this.reloadAllocationState('Material allocation was saved.'),
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'Allocation Failed',
                detail: error?.error?.message || error?.error || 'Unable to allocate reserved material.'
            })
        });
    }

    removeAllocation(allocation: PosmDeliveryScheduleAllocation): void {
        if (!this.projectId || !this.companyId || !this.lineId || !allocation.id) return;
        this.savingAllocationId.set(allocation.id);
        this.posmService.deleteDeliveryScheduleAllocation(this.projectId, this.lineId, allocation.id, this.companyId)
            .pipe(finalize(() => this.savingAllocationId.set(null)))
            .subscribe({
                next: () => this.reloadAllocationState('Material allocation was removed.'),
                error: error => this.messageService.add({
                    severity: 'error',
                    summary: 'Allocation Failed',
                    detail: error?.error?.message || 'Unable to remove material allocation.'
                })
            });
    }

    updateAllocationQuantity(sourceId: number | undefined, value: any): void {
        if (!sourceId) return;
        this.allocationQuantities.update(current => ({
            ...current,
            [sourceId]: Number(value) || 0
        }));
    }

    private reloadAllocationState(successMessage: string): void {
        if (!this.projectId || !this.companyId || !this.lineId) return;
        this.messageService.add({ severity: 'success', summary: 'Allocation', detail: successMessage });
        this.posmService.getProjectDeliverySchedule(this.projectId, this.lineId, this.companyId).subscribe({
            next: row => {
                this.schedule.set(this.normalizeRow(row));
                this.returnOutcomeConditionId.set(row.returnConditionId || null);
                this.loadAllocationSources();
            },
            error: () => {}
        });
        this.loadAllocations();
    }

    remainingAllocationQuantity(): number {
        return Math.max(Number(this.schedule().remainingAllocationQuantity || 0), 0);
    }

    maxAllocationForCandidate(candidate: PosmDeliveryScheduleAllocation): number {
        return Math.max(Math.min(
            Number(candidate.availableToAllocate || 0),
            this.remainingAllocationQuantity()
        ), 0);
    }

    allocationSeverity(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'ALLOCATED':
                return 'success';
            case 'PARTIAL':
                return 'warning';
            case 'AVAILABLE':
                return 'info';
            default:
                return 'secondary';
        }
    }

    sourceLabel(source?: PosmDeliveryScheduleAllocation): string {
        if (!source) return '-';
        return source.sourceWarehouseName || source.supplierClientName || source.sourceType || `Source #${source.projectItemSourceId}`;
    }

    coverageMode(allocation?: PosmDeliveryScheduleAllocation): 'PHYSICAL' | 'LOGICAL' {
        return `${allocation?.coverageMode || this.schedule().coverageMode || ''}`.toUpperCase() === 'LOGICAL'
            ? 'LOGICAL'
            : 'PHYSICAL';
    }

    coverageModeLabel(allocation?: PosmDeliveryScheduleAllocation): string {
        return this.coverageMode(allocation) === 'LOGICAL' ? 'Supplier-confirmed supply' : 'Stock-backed supply';
    }

    coverageModeSeverity(allocation?: PosmDeliveryScheduleAllocation): string {
        return this.coverageMode(allocation) === 'LOGICAL' ? 'info' : 'secondary';
    }

    isLogicalCoverage(allocation?: PosmDeliveryScheduleAllocation): boolean {
        return this.coverageMode(allocation) === 'LOGICAL';
    }

    allocationSummaryLabel(allocation?: PosmDeliveryScheduleAllocation): string {
        return this.isLogicalCoverage(allocation)
            ? 'Logical source coverage'
            : 'Allocated reserved stock unit';
    }

    candidateSummaryLabel(candidate?: PosmDeliveryScheduleAllocation): string {
        return this.isLogicalCoverage(candidate)
            ? 'Logical source coverage candidate'
            : 'Reserved stock unit candidate';
    }

    sourceDetailsForAllocation(allocation?: PosmDeliveryScheduleAllocation): PosmProjectItemSource | null {
        const sourceId = allocation?.projectItemSourceId;
        return sourceId ? this.allocationSources()[sourceId] || null : null;
    }

    reservationForAllocation(allocation?: PosmDeliveryScheduleAllocation): PosmProjectItemSourceReservation | null {
        const reservations = this.sourceDetailsForAllocation(allocation)?.reservations || [];
        if (!reservations.length) {
            return null;
        }
        return reservations.find(reservation =>
            (!!allocation?.reservationAllocationId && reservation.id === allocation.reservationAllocationId)
            || (!!allocation?.stockUnitId && reservation.stockUnitId === allocation.stockUnitId)
        ) || null;
    }

    reservationPillLabel(reservation: PosmProjectItemSourceReservation): string {
        const code = reservation.stockUnitCode || `Unit #${reservation.stockUnitId || '-'}`;
        const zone = reservation.zoneName || reservation.warehouseName || '-';
        const qty = this.formatQuantity(Number(reservation.quantity || 0));
        return `${code} | ${zone} | Qty ${qty}`;
    }

    allocationReservationLabel(allocation?: PosmDeliveryScheduleAllocation): string {
        const reservation = this.reservationForAllocation(allocation);
        if (reservation) {
            return this.reservationPillLabel(reservation);
        }
        if (this.isLogicalCoverage(allocation)) {
            const source = this.sourceDetailsForAllocation(allocation);
            const warehouse = allocation?.sourceWarehouseName
                || source?.sourceWarehousingWarehouseName
                || source?.sourceWarehouseName
                || source?.supplierClientName
                || allocation?.supplierClientName
                || '-';
            const qty = this.formatQuantity(Number(allocation?.availableToAllocate || allocation?.reservedQuantity || allocation?.allocatedQuantity || 0));
            return [warehouse, `Qty ${qty}`, 'External coverage'].join(' | ');
        }
        const code = allocation?.stockUnitCode || (allocation?.stockUnitId ? `Unit #${allocation.stockUnitId}` : null);
        const zone = allocation?.zoneName || allocation?.sourceWarehouseName || '-';
        const qty = this.formatQuantity(Number(allocation?.reservedQuantity || allocation?.allocatedQuantity || 0));
        return [code || 'Legacy source allocation', zone, `Qty ${qty}`].join(' | ');
    }

    private allocationRequestKey(allocation?: PosmDeliveryScheduleAllocation): number | null {
        return allocation?.reservationAllocationId || allocation?.projectItemSourceId || null;
    }

    formatQuantity(value?: number): string {
        const amount = Number(value || 0);
        return amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
    }

    openLocation(locationId?: number): void {
        const resolvedLocationId = locationId || this.schedule().location?.id || this.selectedStore()?.location?.id;
        if (resolvedLocationId) {
            this.openInDialog('Location', `/location/view/${resolvedLocationId}`);
        }
    }

    openItem(): void {
        const itemId = this.schedule().inventoryItem?.id || this.selectedItem()?.inventoryItem?.id;
        if (itemId) {
            this.openInDialog('Warehousing Item', `/warehousing/items/view/${itemId}`);
        }
    }

    openRelease(): void {
        const releaseId = this.schedule().transportReleaseId;
        if (releaseId) {
            this.openInDialog('Order Release', `/transport-planning/releases/${releaseId}`);
        }
    }

    openRemovalRelease(): void {
        const releaseId = this.schedule().removalTransportReleaseId;
        if (releaseId) {
            this.openInDialog('Removal Release', `/transport-planning/releases/${releaseId}`);
        }
    }

    openDelivery(): void {
        const deliveryId = this.schedule().deliveryId;
        if (deliveryId) {
            this.openInDialog('Delivery', `/shipment/delivery/view/${deliveryId}`);
        }
    }

    openRemovalDelivery(): void {
        const deliveryId = this.schedule().removalDeliveryId;
        if (deliveryId) {
            this.openInDialog('Removal Delivery', `/shipment/delivery/view/${deliveryId}`);
        }
    }

    completeReturnOutcome(): void {
        if (!this.projectId || !this.companyId || !this.lineId) {
            return;
        }
        const row = this.schedule();
        if (!this.canCompleteReturnOutcome()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Return Outcome',
                detail: 'Return outcome completion is available only after the removal delivery is completed.'
            });
            return;
        }
        if (this.requiresWarehouseReturnCompletion() && !this.returnOutcomeZoneId()) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Return Outcome',
                detail: 'Select the receiving zone first.'
            });
            return;
        }
        const conditionId = this.returnOutcomeConditionId() || row.returnConditionId || undefined;
        if (this.requiresConditionForReturnCompletion() && !conditionId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Return Outcome',
                detail: 'Select the return item condition first.'
            });
            return;
        }

        this.completingReturnOutcome.set(true);
        this.posmService.completeReturnOutcome(this.projectId, this.lineId, this.companyId, {
            zoneId: this.returnOutcomeZoneId() || undefined,
            conditionId,
            notes: this.returnOutcomeNotes() || undefined
        }).pipe(finalize(() => this.completingReturnOutcome.set(false))).subscribe({
            next: row => {
                this.schedule.set(this.normalizeRow(row));
                this.returnOutcomeNotes.set('');
                this.messageService.add({
                    severity: 'success',
                    summary: 'Return Outcome',
                    detail: 'The removal return outcome was completed.'
                });
            },
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'Return Outcome',
                detail: error?.error?.message || error?.error || 'Unable to complete the return outcome.'
            })
        });
    }

    openProject(): void {
        if (this.projectId) {
            this.openInDialog('POSM Execution', `/posm/executions/${this.projectId}`);
        }
    }

    openClient(): void {
        const clientId = this.project()?.crmClientId || this.project()?.crmClient?.id;
        if (clientId) {
            this.openInDialog('Client', `/crm/clients/${clientId}`);
        }
    }

    closeViewerDialog(): void {
        this.showViewerDialog.set(false);
        this.viewerDialogUrl.set(null);
    }

    backToSchedule(): void {
        if (this.projectId) {
            this.router.navigate(['/posm/executions', this.projectId], { queryParams: { tab: 'delivery-schedule' } });
        } else {
            this.router.navigate(['/posm/executions']);
        }
    }

    canEdit(): boolean {
        return this.permissionService.hasPermission(app_permissions.posm.projects.edit);
    }

    editSchedule(): void {
        if (this.projectId && this.lineId) {
            this.router.navigate(['/posm/executions', this.projectId, 'delivery-schedule', this.lineId, 'edit']);
        }
    }

    projectClientLabel(): string {
        const project = this.project();
        return project?.crmClient?.companyName || project?.client?.name || '-';
    }

    storeContextLabel(): string {
        const store = this.selectedStore();
        const row = this.schedule();
        return store ? this.storeOptionLabel(store) : row.storeName || row.storeNumber || 'Select a target store';
    }

    itemContextLabel(): string {
        const item = this.selectedItem();
        const row = this.schedule();
        return item ? this.itemOptionLabel(item) : row.standName || 'Select a warehousing item';
    }

    contactContextLabel(): string {
        const store = this.selectedStore();
        const name = store?.contactPersonName || store?.location?.contactPerson || 'Default contact';
        const phone = this.defaultStoreContact(store) || this.schedule().contactNumber || '-';
        return `${name} | ${phone}`;
    }

    private openInDialog(title: string, route: string): void {
        const baseUrl = window.location.origin.replace(/\/$/, '');
        this.viewerDialogHeader.set(title);
        this.viewerDialogUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(`${baseUrl}${route}`));
        this.showViewerDialog.set(true);
    }

    installationStatusLabel(): string {
        const normalized = `${this.schedule().readinessStatus || ''}`.toUpperCase();
        if (normalized === 'READY') return 'Install ready';
        if (normalized.includes('MAPPING')) return 'Install setup needed';
        return 'Install planning pending';
    }

    removalStatusLabel(): string {
        const normalized = `${this.schedule().removalReadinessStatus || ''}`.toUpperCase();
        if (normalized === 'READY') return 'Removal ready';
        if (normalized.includes('MAPPING')) return 'Removal setup needed';
        return 'Removal planning pending';
    }

    executionProgressLabel(): string {
        return this.friendlyStatusLabel(this.schedule().progressStage || this.schedule().status, 'Not started');
    }

    friendlyStatusLabel(value: string | null | undefined, fallback = 'Pending'): string {
        const normalized = `${value || ''}`.trim().toUpperCase();
        switch (normalized) {
            case 'DELIVERED':
                return 'Delivered';
            case 'IN_TRANSIT':
                return 'In transit';
            case 'TRUCK_PLANNED':
                return 'Truck planned';
            case 'SCHEDULED':
                return 'Scheduled';
            case 'TASK_CREATED':
                return 'Task created';
            case 'MAPPING_REQUIRED':
                return 'Setup needed';
            case 'RELEASE_ERROR':
                return 'Release issue';
            case 'EXCEPTION':
                return 'Execution issue';
            case 'NOT_RELEASED':
                return 'Not released';
            case 'NEW':
            case 'PENDING':
            case '':
                return fallback;
            default:
                return this.toTitleCase(normalized.replace(/_/g, ' '));
        }
    }

    allocationLabel(): string {
        switch (`${this.schedule().allocationStatus || ''}`.toUpperCase()) {
            case 'ALLOCATED':
                return 'Materials allocated';
            case 'PARTIAL':
                return 'Materials partly allocated';
            default:
                return 'Material allocation pending';
        }
    }

    returnOutcomeLabel(): string {
        const normalized = `${this.schedule().returnOutcomeStatus || ''}`.trim().toUpperCase();
        if (!normalized) return 'Return not started';
        switch (normalized) {
            case 'PENDING_RETURN':
                return 'Return pending';
            case 'RETURN_DELIVERED':
                return 'Return delivered';
            case 'RECEIPT_COMPLETED':
                return 'Return received';
            case 'DESTRUCTION_DRAFTED':
                return 'Destruction drafted';
            case 'REFURBISHMENT_REQUESTED':
                return 'Refurbishment requested';
            case 'COMPLETED':
                return 'Return completed';
            default:
                return this.toTitleCase(normalized.replace(/_/g, ' '));
        }
    }

    readinessSeverity(value: string | null | undefined): string {
        const normalized = `${value || ''}`.toUpperCase();
        if (normalized === 'READY') return 'success';
        if (normalized.includes('MAPPING')) return 'warning';
        return 'info';
    }

    statusSeverity(status: string | null | undefined): string {
        const normalized = `${status || ''}`.trim().toUpperCase();
        if (!normalized) return 'info';
        if (normalized.includes('COMPLETE') || normalized.includes('DONE') || normalized.includes('DELIVERED') || normalized.includes('APPROVED')) return 'success';
        if (normalized.includes('PENDING') || normalized.includes('NEW') || normalized.includes('DRAFT')) return 'warning';
        if (normalized.includes('CANCEL') || normalized.includes('HOLD') || normalized.includes('ERROR') || normalized.includes('REJECT')) return 'danger';
        return 'info';
    }

    formattedDate(value: unknown): string {
        if (!value) return '-';
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
    }

    hasRemovalStreamInput(row: PosmDeliverySchedule = this.schedule()): boolean {
        if (!this.supportsRemovalFlow()) {
            return false;
        }
        return !!(
            row.removalDate
            || row.plannedRemovalDate
            || row.plannedRemovalDateOverride
            || row.returnDestinationType
            || row.returnWarehouseId
            || row.returnLocationId
            || row.returnDisposition
            || row.removalTaskId
            || row.removalTransportReleaseId
            || row.removalDeliveryId
        );
    }

    isWarehouseReturnDestination(): boolean {
        const destinationType = `${this.schedule().returnDestinationType || ''}`.toUpperCase();
        return destinationType === 'SAME_SOURCE_WAREHOUSE' || destinationType === 'RETURN_WAREHOUSE';
    }

    effectiveReturnWarehouseId(): number | undefined {
        return this.isWarehouseReturnDestination() ? this.schedule().returnWarehouseId : undefined;
    }

    requiresWarehouseReturnCompletion(): boolean {
        return this.isWarehouseReturnDestination();
    }

    requiresConditionForReturnCompletion(): boolean {
        const disposition = `${this.schedule().returnDisposition || ''}`.toUpperCase();
        return disposition === 'DIRECT_RECEIPT' || this.requiresWarehouseReturnCompletion();
    }

    canCompleteReturnOutcome(): boolean {
        if (!this.supportsRemovalFlow()) {
            return false;
        }
        if (!this.schedule().returnDisposition || !this.schedule().returnDestinationType) {
            return false;
        }
        if (!this.schedule().removalDeliveryId) {
            return false;
        }
        if (this.isRemovalOutcomeCompleted()) {
            return false;
        }
        const shipmentStatus = `${this.schedule().removalShipmentStatus || ''}`.toUpperCase();
        return !shipmentStatus || ['DELIVERED', 'COMPLETED', 'RETURN_DELIVERED'].includes(shipmentStatus);
    }

    isRemovalOutcomeCompleted(): boolean {
        const status = `${this.schedule().returnOutcomeStatus || ''}`.toUpperCase();
        return ['RECEIPT_COMPLETED', 'DESTRUCTION_DRAFTED', 'REFURBISHMENT_REQUESTED', 'COMPLETED'].includes(status);
    }

    returnOutcomeActionLabel(): string {
        switch (`${this.schedule().returnDisposition || ''}`.toUpperCase()) {
            case 'DIRECT_RECEIPT':
                return 'Complete Direct Receipt';
            case 'DESTRUCTION':
                return 'Complete Destruction Return';
            case 'REFURBISHMENT':
                return 'Complete Refurbishment Return';
            default:
                return 'Complete Return Outcome';
        }
    }

    hasClientLink(): boolean {
        return !!(this.project()?.crmClientId || this.project()?.crmClient?.id);
    }

    private normalizeRow(row: PosmDeliverySchedule): PosmDeliverySchedule {
        return {
            ...row,
            jobDate: this.toDate(row.jobDate),
            installationDate: this.toDate(row.installationDate),
            plannedInstallationDate: this.toDate(row.plannedInstallationDate),
            plannedInstallationDateOverride: this.toDate(row.plannedInstallationDateOverride),
            removalDate: this.toDate(row.removalDate),
            plannedRemovalDate: this.toDate(row.plannedRemovalDate),
            plannedRemovalDateOverride: this.toDate(row.plannedRemovalDateOverride),
            releaseGeneratedAt: this.toDate(row.releaseGeneratedAt),
            releasedToTruckPlanAt: this.toDate(row.releasedToTruckPlanAt),
            actualDeliveryDate: this.toDate(row.actualDeliveryDate),
            statusDate: this.toDate(row.statusDate),
            returnOutcomeCompletedAt: this.toDate(row.returnOutcomeCompletedAt)
        };
    }

    private toDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }

    private toTitleCase(value: string): string {
        return value
            .toLowerCase()
            .split(' ')
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }

    private storeOptionLabel(store: PosmProjectStore): string {
        const name = store.location?.name || store.storeName || (store.storeNumber ? `Store ${store.storeNumber}` : `Store #${store.id}`);
        const reference = store.location?.externalLocationReference || store.storeNumber;
        return [name, reference, store.city].filter(Boolean).join(' | ');
    }

    private itemOptionLabel(item: PosmProjectItem): string {
        const inventory = item.inventoryItem || {};
        const name = inventory.itemName || inventory.itemDescription || item.missingItemReference || `Item #${item.id}`;
        const reference = inventory.skuNumber || inventory.itemNumber || inventory.customerItemNumber;
        return [name, reference, `Qty ${item.quantity || 0}`].filter(Boolean).join(' | ');
    }

    private defaultStoreContact(store?: PosmProjectStore): string | undefined {
        return store?.contactPersonPhone || store?.location?.telephoneNumber || undefined;
    }

}
